import { isEmpty, isNil, isString } from 'lodash';
import React, { ReactNode, memo, useContext, useMemo } from 'react';
import { FieldLabel } from '@shared/components/FieldLabel';
import { View } from '@shared/components/View';
import { MBIconV2, MBIconV2Name } from '@shared/components/image';
import { IsCardBeforePaidCutoffHandler } from '@shared/data/billing/hooks/useIsCardBeforePaidCutoff';
import {
  getComponentWithOverrides,
  isComponentHidden as getIsComponentHidden,
  isComponentExcludedFromCollectionView,
} from '@shared/data/cards/utils';
import type { Entity } from '@shared/data/entity';
import { CollectionViewAnalytics } from '@shared/scenes/cards/collectionView/CollectionViewAnalytics';
import {
  CollectionViewCard,
  CollectionViewCardStyles,
} from '@shared/scenes/cards/collectionView/components/CollectionViewCard';
import {
  CardCollectionViewField,
  FieldStyles,
} from '@shared/scenes/cards/collectionView/components/fields/CardCollectionViewField';
import { OpenEntityHandler } from '@shared/scenes/cards/collectionView/hooks';
import {
  ChecklistTagComponent,
  getComponentIcon,
  getComponentLabel,
  isChecklistTagComponent,
  isInputComponent,
  isManagingInputComponent,
} from '@shared/scenes/workflows/utils';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { ChecklistButton } from './ChecklistButton';
import { headerFieldStyles, wideCardFieldStyles } from './listViewCardStyles';
import CardStyles from './Card.module.css';

export interface CardProps {
  viewTemplate: API.ViewTemplateFragment;
  workflowTemplate: API.WorkflowTemplateFragment;
  entity: Entity;
  openEntity?: OpenEntityHandler;
  getIsCardBeforePaidCutoff: IsCardBeforePaidCutoffHandler;
  canUpdateCard?: boolean;
  analytics?: CollectionViewAnalytics;
  isRedacted?: boolean;
  hasOverflowMenu?: boolean;
  cardStyles?: CollectionViewCardStyles;
  isBoard?: boolean;
  testID?: string;
}

export const Card = memo(function CardComponent({
  viewTemplate,
  workflowTemplate,
  openEntity,
  getIsCardBeforePaidCutoff,
  entity,
  analytics,
  canUpdateCard = false,
  isRedacted = false,
  hasOverflowMenu = true,
  cardStyles,
  isBoard,
  testID,
}: CardProps) {
  const [firstComponent, otherComponents] = useVisibleComponents({
    workflowTemplate,
    viewTemplate,
    entity,
  });

  const checklistComponent = getChecklistComponent({ viewTemplate, workflowTemplate });

  return (
    <View flex testID={testID} className={[CardStyles.card, isBoard && CardStyles.board]}>
      <CollectionViewCard
        workflowTemplate={workflowTemplate}
        entity={entity}
        openEntity={openEntity}
        getIsCardBeforePaidCutoff={getIsCardBeforePaidCutoff}
        hasOverflowMenu={hasOverflowMenu}
        cardStyles={cardStyles}
      >
        <RedactedWrapper isRedacted={isRedacted}>
          <CheckListWrapper
            entity={entity}
            checklistComponent={checklistComponent}
            viewTemplateId={viewTemplate.id}
          >
            {!isNil(firstComponent) && (
              <CardCollectionViewComponent
                component={firstComponent}
                entity={entity}
                workflowTemplateId={workflowTemplate.id}
                fieldStyles={headerFieldStyles}
                showLabel={isRedacted ? false : !viewTemplate.defaultHiddenLabels}
                analytics={analytics}
                canUpdateCard={canUpdateCard}
                isRedacted={isRedacted}
              />
            )}
            <View className={CardStyles.otherComponents}>
              {!isRedacted &&
                otherComponents.map(component => {
                  return (
                    <CardCollectionViewComponent
                      key={component.id}
                      component={component}
                      entity={entity}
                      workflowTemplateId={workflowTemplate.id}
                      fieldStyles={wideCardFieldStyles}
                      showLabel={!viewTemplate.defaultHiddenLabels}
                      analytics={analytics}
                      canUpdateCard={canUpdateCard}
                      isRedacted={false}
                    />
                  );
                })}
              {isRedacted && <View className={CardStyles.redactedPlaceholder} />}
            </View>
          </CheckListWrapper>
        </RedactedWrapper>
      </CollectionViewCard>
    </View>
  );
});

function RedactedWrapper({
  children,
  isRedacted,
}: {
  children: React.ReactNode;
  isRedacted: boolean;
}) {
  return <View className={isRedacted && CardStyles.redacted}>{children}</View>;
}

function CheckListWrapper({
  children,
  entity,
  checklistComponent,
  viewTemplateId,
}: {
  children: ReactNode;
  entity: Entity;
  checklistComponent?: ChecklistTagComponent;
  viewTemplateId: string;
}) {
  if (isNil(checklistComponent)) {
    return <>{children}</>;
  } else {
    return (
      <View row className={CardStyles.checklistWrapper}>
        <ChecklistButton
          entity={entity}
          checklistComponent={checklistComponent}
          viewTemplateId={viewTemplateId}
        />
        <View fill>{children}</View>
      </View>
    );
  }
}

function useVisibleComponents({
  workflowTemplate,
  viewTemplate,
  entity,
}: {
  workflowTemplate: API.WorkflowTemplateFragment;
  viewTemplate: API.ViewTemplateFragment;
  entity: Entity;
}): [API.AnyComponent, API.AnyComponent[]] {
  return useMemo(() => {
    const filtered = workflowTemplate.components
      .filter(component => {
        const viewOptions = viewTemplate.componentsViewOptions?.find(
          options => options.componentId === component.id,
        );
        const field = entity.fields.get(component.id);
        const isHiddenWhenEmpty =
          isInputComponent(component) || isManagingInputComponent(component);
        const isFieldEmpty = isNil(field) || field.isEmpty;
        const isComponentHidden = getIsComponentHidden(viewTemplate, viewOptions, component);
        return (
          !isComponentHidden &&
          (!isFieldEmpty || !isHiddenWhenEmpty) &&
          !isComponentExcludedFromCollectionView(component)
        );
      })
      .map(component => getComponentWithOverrides(component, viewTemplate));
    const [firstComponent, ...otherComponents] = filtered;
    return [firstComponent, otherComponents];
  }, [workflowTemplate, viewTemplate, entity]);
}

function getChecklistComponent({
  viewTemplate,
  workflowTemplate,
}: {
  viewTemplate: API.ViewTemplateFragment;
  workflowTemplate: API.WorkflowTemplateFragment;
}): ChecklistTagComponent | undefined {
  if (viewTemplate.subtype !== 'LIST') {
    return undefined;
  }
  const checklistComponent = workflowTemplate.components
    .filter(isChecklistTagComponent)
    .find(component => component.id === viewTemplate.checklistTagComponentId);
  return checklistComponent;
}

interface CardCollectionViewComponentProps {
  component: API.AnyComponent;
  entity: Entity;
  workflowTemplateId: string;
  fieldStyles: FieldStyles;
  showLabel: boolean;
  analytics?: CollectionViewAnalytics;
  canUpdateCard: boolean;
  isRedacted?: boolean;
}

const CardCollectionViewComponent = memo(function CardCollectionViewComponentMemo({
  component,
  entity,
  workflowTemplateId,
  fieldStyles,
  showLabel,
  analytics,
  canUpdateCard,
  isRedacted = false,
}: CardCollectionViewComponentProps) {
  const colors = useContext(ColorThemeContext);
  const field = entity.fields.get(component.id);
  const componentIconName = getComponentIcon(component);
  const componentLabel = getComponentLabel(component);

  return (
    <View
      className={[
        CardStyles.component,
        isRedacted && CardStyles.redacted,
        showLabel && CardStyles.showLabel,
      ]}
    >
      {!isEmpty(componentIconName) && (
        <View paddingTop={0.3}>
          <MBIconV2 name={componentIconName as MBIconV2Name} size={grid(4)} color={colors.accent} />
        </View>
      )}
      <View paddingHorizontal={1} fill>
        {showLabel && isString(componentLabel) && !isEmpty(componentLabel) && (
          <FieldLabel label={componentLabel} />
        )}
        <CardCollectionViewField
          field={field}
          workflowTemplateId={workflowTemplateId}
          entityId={entity.id}
          component={component}
          fieldStyles={fieldStyles}
          showLabel={showLabel}
          canUpdateCard={canUpdateCard}
          analytics={analytics}
        />
      </View>
    </View>
  );
});
